import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSwitcher } from "../components/app/LanguageSwitcher";
import { toast } from "sonner";
import {
  captureSourceFromUrl,
  clearStoredSource,
  readStoredSource,
} from "../lib/acquisitionSource";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, lang } = useT();
  const [form, setForm] = useState({ business_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  // Direct hits to /register?source=... (e.g. an SMS that skips /free) should
  // also be captured so attribution doesn't depend on the user passing
  // through the landing page first.
  useEffect(() => {
    captureSourceFromUrl();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Read whatever attribution was captured on /free or directly here.
      // Clear it AFTER a successful register so refreshing the page or
      // signing up a second account from the same browser doesn't carry
      // the same tag forever.
      const source = readStoredSource();
      await register(form.email, form.password, form.business_name, source, lang);
      clearStoredSource();
      toast.success(t("auth.accountCreatedToast"));
      navigate("/dashboard");
    } catch (err) {
      // FastAPI 422s return `detail` as an array of {type, loc, msg, input, ctx};
      // pass that straight into toast.error and React crashes (Objects are not
      // valid as a React child). Normalise to a string before display.
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg).filter(Boolean).join(", ")
        : (typeof detail === "string" ? detail : "");
      toast.error(msg || t("auth.registrationFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f9f9f8]" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-800 to-green-900 p-12 text-white items-center">
        <div className="max-w-md">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display font-bold text-xl">Tasklio AI</span>
          </Link>
          <h2 className="font-display mt-16 text-4xl font-semibold tracking-tight leading-tight">
            {t("auth.registerSidebarTitle")}
          </h2>
          <ul className="mt-8 space-y-3 text-green-50">
            {[
              t("auth.registerSidebarPoint1"),
              t("auth.registerSidebarPoint2"),
              t("auth.registerSidebarPoint3"),
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-200 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="lg:hidden inline-flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </span>
              <span className="font-display font-bold text-lg">Tasklio AI</span>
            </Link>
            <div className="ml-auto">
              <LanguageSwitcher testidPrefix="register-lang-switch" />
            </div>
          </div>
          <h1 className="font-display text-3xl font-semibold text-gray-900">{t("auth.registerTitle")}</h1>
          <p className="mt-2 text-gray-500">{t("auth.registerSubtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="business_name">{t("auth.businessName")}</Label>
              <Input
                id="business_name"
                value={form.business_name}
                onChange={set("business_name")}
                placeholder={t("auth.businessPlaceholder")}
                required
                className="mt-2 h-11"
                data-testid="register-business-input"
              />
            </div>
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t("auth.emailPlaceholder")}
                required
                className="mt-2 h-11"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={set("password")}
                placeholder={t("auth.passwordPlaceholder")}
                required
                minLength={6}
                className="mt-2 h-11"
                data-testid="register-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-green-800 hover:bg-green-900 text-white"
              data-testid="register-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.createAccount")}
            </Button>
            <p
              className="text-xs text-gray-500 leading-relaxed"
              data-testid="register-sms-notice"
            >
              {t("register.smsNotice")}
              <Link
                to="/sms-opt-in"
                className="text-green-800 hover:underline font-medium"
                data-testid="register-sms-opt-in-link"
              >
                {t("register.smsNoticeLink")}
              </Link>
              .
            </p>
          </form>

          <p className="mt-8 text-sm text-gray-500 text-center">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link to="/login" className="text-green-800 hover:underline font-medium" data-testid="register-login-link">
              {t("auth.logInLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
